import React, { useState } from 'react';
import { Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import { APPLICATION_NAME } from '../constants/AppConfig';
import AppLogo from '../images/logo.png';
import './LoginPage.css';

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const validate = (): boolean => {
    if (!email.trim()) {
      setEmailError(t('login.emailRequired'));
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailError(t('login.emailInvalid'));
      return false;
    }
    setEmailError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await apiService.post('/auth/forgot-password', { email: email.trim() });
    } catch {
      // Intentionally swallowed — the backend always returns 200 so the caller
      // cannot probe whether the email is registered.
    } finally {
      setLoading(false);
      setSent(true);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img src={AppLogo} alt={APPLICATION_NAME} className="login-logo" />
            <h1 className="login-title">{t('forgotPassword.title')}</h1>
            <p className="login-subtitle">{APPLICATION_NAME}</p>
          </div>

          {sent ? (
            <div className="fp-sent-state">
              <div className="fp-sent-icon">
                <Mail size={40} strokeWidth={1.5} />
              </div>
              <p className="fp-sent-message">{t('forgotPassword.sentMessage')}</p>
              <button
                type="button"
                className="btn-save"
                onClick={() => navigate('/login')}
              >
                {t('forgotPassword.backToLogin')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="login-form" noValidate>
              <p className="fp-instructions">{t('forgotPassword.instructions')}</p>

              <div className="form-group">
                <label className="form-label" htmlFor="fp-email">
                  {t('login.email')}
                </label>
                <input
                  id="fp-email"
                  type="email"
                  className={`form-control${emailError ? ' is-invalid' : ''}`}
                  placeholder={t('login.emailPlaceholder')}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailError(null); }}
                  disabled={loading}
                  autoComplete="email"
                  autoFocus
                  maxLength={150}
                />
                {emailError && <small className="form-error">{emailError}</small>}
              </div>

              <button type="submit" className="btn-save" disabled={loading}>
                {loading ? t('forgotPassword.sending') : t('forgotPassword.sendLink')}
              </button>

              <button
                type="button"
                className="fp-back-link"
                onClick={() => navigate('/login')}
              >
                {t('forgotPassword.backToLogin')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
